import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Trash, ArrowCounterClockwise, CaretRight } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const TOOL_LABELS = {
  content_studio: 'Content Studio',
  copy_studio: 'Copy Studio',
  brand_analyzer: 'Brand Analyzer',
  seo: 'SEO Assistant',
  email: 'Email Campaign',
  meta_ads: 'Meta Ads',
  ugc_script: 'UGC Script',
  creator_brief: 'Creator Brief',
  influencer_brief: 'Influencer Brief',
  outreach_email: 'Outreach Email',
};

const SUB_TYPE_LABELS = {
  ideas: 'Content Ideas', caption: 'Captions', hooks: 'Hooks', hashtags: 'Hashtags',
  reel_script: 'Reel Script', carousel: 'Carousel Copy',
  product_description: 'Product Description', homepage: 'Homepage Copy',
  landing_page: 'Landing Page', about: 'About Page',
  email_newsletter: 'Newsletter', ad_copy: 'Ad Copy', social_bio: 'Social Bio',
  welcome: 'Welcome Email', launch: 'Product Launch', promo: 'Promotional',
  reengagement: 'Re-engagement', abandoned_cart: 'Abandoned Cart', newsletter: 'Newsletter',
};

export const TextHistoryPanel = ({ toolType, onReload }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/text-history/${toolType}`);
      setHistory(data.history || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [toolType]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const deleteItem = async (historyId) => {
    try {
      await axios.delete(`${API}/api/text-history/${historyId}`);
      setHistory(prev => prev.filter(h => h.history_id !== historyId));
      toast.success('Removed from history');
    } catch { toast.error('Failed to delete'); }
  };

  const getPreview = (item) => {
    const inputs = item.inputs || {};
    if (toolType === 'copy_studio') return inputs.product_service || inputs.copy_type || 'Copy';
    if (toolType === 'content_studio') return inputs.niche || inputs.content_type || 'Content';
    if (toolType === 'brand_analyzer') return inputs.website_url || inputs.brand_description?.slice(0, 60) || 'Analysis';
    if (toolType === 'seo') return inputs.niche || inputs.product_service || 'SEO';
    if (toolType === 'email') return inputs.goal || inputs.campaign_type || 'Email';
    if (toolType === 'meta_ads') return inputs.product_service || inputs.objective || 'Meta Ads';
    if (toolType === 'ugc_script') return inputs.product || inputs.platform || 'UGC Script';
    if (toolType === 'creator_brief') return inputs.brand_name || inputs.product || 'Creator Brief';
    if (toolType === 'influencer_brief') return inputs.brand_name || inputs.product || 'Influencer Brief';
    if (toolType === 'outreach_email') return inputs.influencer_name || inputs.brand_name || 'Outreach';
    return 'Generation';
  };

  const getSubLabel = (item) => SUB_TYPE_LABELS[item.sub_type] || item.sub_type || '';

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-5 h-5 border-2 border-white/10 border-t-white/40 rounded-full animate-spin" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-16" data-testid="history-empty">
        <Clock size={32} className="mx-auto mb-3 opacity-20" style={{ color: '#f5f0e8' }} />
        <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No history yet</p>
        <p className="text-xs mt-1" style={{ color: 'rgba(245,240,232,0.15)' }}>
          Your generations will appear here automatically
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="history-panel">
      {history.map((item) => (
        <motion.div
          key={item.history_id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="group rounded-xl p-4 cursor-pointer transition-all hover:ring-1 hover:ring-white/10"
          style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}
          onClick={() => onReload?.(item)}
          data-testid={`history-item-${item.history_id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {getSubLabel(item) && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                    style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.15)' }}>
                    {getSubLabel(item)}
                  </span>
                )}
                <span className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>
                  {formatDate(item.created_at)}
                </span>
              </div>
              <p className="text-sm font-medium truncate" style={{ color: '#f5f0e8' }}>
                {getPreview(item)}
              </p>
              {item.inputs && (
                <p className="text-xs mt-1 truncate" style={{ color: 'rgba(245,240,232,0.25)' }}>
                  {Object.entries(item.inputs).filter(([k, v]) => v && k !== 'copy_type' && k !== 'content_type' && k !== 'campaign_type').map(([k, v]) => `${String(v).slice(0, 30)}`).join(' · ')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => { e.stopPropagation(); onReload?.(item); }}
                className="p-1.5 rounded-md hover:bg-white/5"
                title="Load this result"
                data-testid={`reload-history-${item.history_id}`}
              >
                <ArrowCounterClockwise size={14} style={{ color: '#c8f060' }} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); deleteItem(item.history_id); }}
                className="p-1.5 rounded-md hover:bg-red-500/10"
                title="Delete"
                data-testid={`delete-history-${item.history_id}`}
              >
                <Trash size={14} style={{ color: 'rgba(255,68,68,0.5)' }} />
              </button>
              <CaretRight size={12} style={{ color: 'rgba(245,240,232,0.15)' }} />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default TextHistoryPanel;
